import React from 'react';
import { Box, Grid, Typography, Button, List, ListItem, TextField, MenuItem } from "@mui/material";
import swal from "sweetalert";
import { createReport } from '../../utilityFunctions/DataloveUtil';
import CustomLoader from "../../components/CustomLoader";
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import Colors from "../../components/ui/Colors";
import { CONTENT_TOP_OFFSET } from "../../layout/layoutMetrics";

// Styled component for the file input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];

const reportRequest = {
  year: null
};

export default function DataReports(props) {
  const { loginUser } = props;
  const [loading, setLoading] = React.useState(false);
  const [analysisResult, setAnalysisResult] = React.useState(null);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const { t } = useTranslation();
  
  const [year, setYear] = React.useState("");
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      setSelectedFile(file);
    } else {
      swal("Error", "Please select a PDF or Word file", "error");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      swal("Error", "Please select a file first", "error");
      return;
    }

    if (!reportRequest.year) {
      swal("Error", "Please select a year first", "error");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const json = JSON.stringify(reportRequest);
      const newReportRequest = new Blob([json], {
        type: 'application/json'
      });
      formData.append('reportRequest', newReportRequest);

      const result = await createReport(formData, loginUser.jwtToken);
      
      if (!result) {
        swal("Error", "Failed to analyze document", "error");
        return;
      }

      setAnalysisResult(result);
    } catch (error) {
      console.error("Error analyzing document:", error);
      swal("Error", "Failed to analyze document", "error");
    } finally {
      setLoading(false);
    }
  };

  const yearChange = (e) => {
    reportRequest.year = e.target.value;
    setYear(e.target.value);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%" }}>
      <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: {xs: "start", sm: "start", md: "center"},
        width: "95%",
        height: "90%",
        textAlign: "center",
        // One offset for every breakpoint. It used to be 2rem on sm / 0 on md,
        // i.e. less than the fixed navbar's height, so the top of the column sat
        // behind the bar and only the heading's own 6rem margin saved it.
        marginTop: CONTENT_TOP_OFFSET
      }}>
        {loading ? (
          <CustomLoader />
        ) : (
          <Grid container direction="row" spacing={2} sx={{display: "flex", justifyContent: "center"}}>
            <Grid item xs={12}>
              <Typography variant="h4" sx={{ marginTop: { xs: "1rem", md: "2rem" }, fontWeight: "bold" }}>
                Document Analysis
              </Typography>
            </Grid>
            {!analysisResult &&
              <>
                <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mb: "2.5rem", }}>
                  <TextField
                      sx={{textAlign: "start", width: {xs: "90%", sm: "60%", md: "40%"}, mt: "2rem"}}
                  variant="standard"
                  fullWidth={true}
                  select
                  label={<Typography variant="body2">Select the year to fetch the data</Typography>}
                  value={year}
                  onChange={(e)=>yearChange(e)}
                  autoComplete="off"
              >
                  {YEARS.map((el) => (
                      <MenuItem key={el} value={el}>
                          {el}
                      </MenuItem>
                  ))}
              </TextField> 
                </Grid>
                <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      backgroundColor: Colors.secondary,
                      '&:hover': {backgroundColor: Colors.hover}
                    }}
                  >
                    Select Template Report
                    <VisuallyHiddenInput type="file" onChange={handleFileChange} accept=".pdf" />
                  </Button>
                </Grid>
                {selectedFile && (
                  <Grid item xs={12} sx={{mb: "1rem"}}>
                    <Typography variant="body1">Selected file: {selectedFile.name}</Typography>
                  </Grid>
                  
                )}
                <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </>
            }
            {analysisResult && (
              <Grid item xs={12} sx={{ mt: "2rem" }}>
                <List sx={{width: "100%", marginTop: "2rem"}}>
                  <ListItem disablePadding sx={{marginBottom: "2rem"}}>
                    <Typography variant="h6">Steps executed</Typography>
                  </ListItem>
                  <ListItem disablePadding sx={{marginBottom: "2rem"}}>
                    <Typography variant="body2">1. Extracted the document’s structure and content</Typography>
                  </ListItem>
                  <ListItem disablePadding sx={{marginBottom: "2rem"}}>
                    <Typography variant="body2">2. Retrieved the requested dataset</Typography>
                  </ListItem>
                  <ListItem disablePadding sx={{marginBottom: "2rem"}}>
                    <Typography variant="body2">3. An AI model generated the report based on the new data</Typography>
                  </ListItem>
                  <ListItem disablePadding sx={{marginBottom: "2rem"}}>
                    <Typography variant="body2">4. An AI agent formatted the report based on the extracted structure</Typography>
                  </ListItem>
                </List>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
